import React, { useState, useRef, useCallback } from 'react';
import './CustomerStoreLogo.css';

import { API_BASE_URL, getMediaUrl } from '../services/api';

interface CustomerStoreLogoProps {
  customerId: number;
  customerName: string;
  customerType: string;
  currentLogoUrl: string | null;
  logoBackgroundColor: string;
  onUpdate: () => void;
}

const CustomerStoreLogo: React.FC<CustomerStoreLogoProps> = ({
  customerId,
  customerName,
  customerType,
  currentLogoUrl,
  logoBackgroundColor,
  onUpdate,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backgroundColor, setBackgroundColor] = useState(logoBackgroundColor || '#FFFFFF');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isRetailPartner = customerType === 'RETAIL_PARTNER';

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isRetailPartner) {
      setIsDragging(true);
    }
  }, [isRetailPartner]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a PNG, JPG, SVG, or WebP file';
    }
    if (file.size > 512000) {
      return 'File must be smaller than 500KB';
    }
    return null;
  };

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('store_logo', file);
    formData.append('logo_background_color', backgroundColor);

    try {
      const response = await fetch(`${API_BASE_URL}/customers/${customerId}/store-logo/`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Upload failed');
      }

      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (!isRetailPartner) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  }, [isRetailPartner, backgroundColor]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the store logo?')) {
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/customers/${customerId}/store-logo/`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Delete failed');
      }

      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to remove logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleBackgroundColorChange = async (newColor: string) => {
    setBackgroundColor(newColor);

    // Only update server if there's already a logo
    if (!currentLogoUrl) return;

    try {
      await fetch(`${API_BASE_URL}/customers/${customerId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ store_logo_background_color: newColor }),
      });
      onUpdate();
    } catch (err) {
      console.error('Failed to update background color:', err);
    }
  };

  if (!isRetailPartner) {
    return (
      <div className="store-logo-section">
        <div className="store-logo-unavailable">
          <h3>Store Logo</h3>
          <p>Store logos for co-branded Bridge Club cards are only available for Retail Partner customers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="store-logo-section">
      <div className="store-logo-header">
        <h3>Store Logo</h3>
        <p className="store-logo-description">
          Upload your store logo to appear on co-branded Bridge Club member cards alongside the Red Bridge logo.
        </p>
      </div>

      {error && (
        <div className="store-logo-error">
          {error}
        </div>
      )}

      {currentLogoUrl ? (
        <div className="store-logo-preview-section">
          <div className="store-logo-preview-card">
            <div className="store-logo-preview-label">Current Logo</div>
            <div
              className="store-logo-preview-container"
              style={{ backgroundColor }}
            >
              <img
                src={getMediaUrl(currentLogoUrl) || ''}
                alt={`${customerName} logo`}
                className="store-logo-preview-image"
              />
            </div>
          </div>

          <div className="store-logo-settings">
            <div className="store-logo-setting">
              <label htmlFor="bg-color">Background Color</label>
              <div className="color-picker-row">
                <input
                  type="color"
                  id="bg-color"
                  value={backgroundColor}
                  onChange={(e) => handleBackgroundColorChange(e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={backgroundColor}
                  onChange={(e) => handleBackgroundColorChange(e.target.value)}
                  className="color-input"
                  placeholder="#FFFFFF"
                  maxLength={7}
                />
              </div>
              <p className="setting-hint">Used behind transparent logos</p>
            </div>
          </div>

          <div className="store-logo-actions">
            <button
              className="btn btn-outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              Replace Logo
            </button>
            <button
              className="btn btn-danger"
              onClick={handleRemoveLogo}
              disabled={isUploading}
            >
              Remove Logo
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`store-logo-dropzone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          {isUploading ? (
            <div className="dropzone-content">
              <div className="upload-spinner"></div>
              <p>Uploading...</p>
            </div>
          ) : (
            <div className="dropzone-content">
              <div className="dropzone-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17,8 12,3 7,8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="dropzone-text">
                <strong>Drop your logo here</strong> or click to browse
              </p>
              <p className="dropzone-hint">
                PNG, JPG, SVG, or WebP - Max 500KB
              </p>
              <p className="dropzone-hint">
                Square or horizontal logos work best
              </p>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Card Preview */}
      <div className="store-logo-card-preview">
        <h4>Bridge Club Card Preview</h4>
        <p className="preview-description">
          This is how your logo will appear on member cards
        </p>
        <div className="card-preview-mini">
          <div className="card-preview-header">
            <img src="/rbf-logo.png" alt="Red Bridge" className="preview-logo-rb" />
            {currentLogoUrl ? (
              <>
                <span className="preview-logo-divider">+</span>
                <div
                  className="preview-logo-store-wrapper"
                  style={{ backgroundColor }}
                >
                  <img
                    src={getMediaUrl(currentLogoUrl) || ''}
                    alt={customerName}
                    className="preview-logo-store"
                  />
                </div>
              </>
            ) : (
              <div className="preview-logo-placeholder">
                Your Logo
              </div>
            )}
          </div>
          <div className="card-preview-badge">BRIDGE CLUB</div>
          <div className="card-preview-qr">
            <div className="qr-placeholder-mini"></div>
          </div>
          <div className="card-preview-member">Member Name</div>
          <div className="card-preview-store">{customerName}</div>
        </div>
      </div>
    </div>
  );
};

export default CustomerStoreLogo;
